/* ============================================================
   스키마 페이지 — 2026-05-12 라이브화 + v2.5 sub_groups label 우선
   useSchemaObjects hook으로 v_dashboard_meta_full 라이브 조회
   TABLES + VIEWS 두 섹션 그리드 (flat-row 패턴)
   - 테이블: 좌측 보더 초록 + Database SVG (14px)
   - 뷰: 좌측 보더 앰버 + Eye SVG (14px) + hover 브라운 본질

   v2.5 (2026-05-12): 헤더 제목 본질 분기 ─ 의장님 결재 옵션 B
   - sub_groups 진입 시: subGroupLabel 우선 표시 (DevTrack 등 영문 Track)
   - 사이드바 진입 시: SCHEMA_LABELS fallback (시스템 메타 등 한글)
   - 다른 영역(사이드바/CategoryView 등) 영향 0

   2026-05-13 App.tsx 분리
   ============================================================ */
import { SCHEMA_LABELS, SCHEMA_DESC } from '../data'
import { ICONS } from '../icons'
import { useSchemaObjects } from '../useSchemaObjects'

export function SchemaView({
  catName, schemaName, subGroupLabel, subGroupDesc, openHome, openCategory,
}: {
  catName: string
  schemaName: string
  subGroupLabel?: string
  subGroupDesc?: string
  openHome: () => void
  openCategory: (name: string) => void
}) {
  const { tables, views, ready, error } = useSchemaObjects(schemaName)
  const totalCount = tables.length + views.length

  /* v2.5: 헤더 제목 본질 분기.
     subGroupLabel 우선 (CategoryView sub_groups 진입 시)
     fallback: SCHEMA_LABELS[schemaName] (사이드바 진입 또는 sub_groups 미정의 카테고리)
     fallback: schemaName 자체 */
  const headerLabel = subGroupLabel || SCHEMA_LABELS[schemaName] || schemaName
  const headerDesc = subGroupDesc || SCHEMA_DESC[schemaName] || ''

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={openHome}>홈</a>
        <span className="sep">/</span>
        <a onClick={() => openCategory(catName)}>{catName}</a>
        <span className="sep">/</span>
        <span>{schemaName}</span>
      </div>
      <div className="page-header">
        <h2>
          {headerLabel}
          <span className="meta-mono">{schemaName}</span>
        </h2>
        <div className="desc">
          {ready
            ? `→ ${totalCount}개 객체 · ${tables.length} tables · ${views.length} views`
            : '→ 로딩 중...'}
          {error && <span style={{ color: 'var(--accent-red)', marginLeft: 12 }}>· {error}</span>}
          {headerDesc && (
            <span style={{ display: 'block', marginTop: 4 }}>{headerDesc}</span>
          )}
        </div>
      </div>

      {/* TABLES 섹션 */}
      {tables.length > 0 && (
        <div className="flat-section">
          <div className="flat-section-title tables">
            <span className="icon">{ICONS.Database(14)}</span>
            <span>TABLES</span>
            <span className="count">{tables.length}</span>
          </div>
          <div className="flat-grid">
            {tables.map(t => (
              <div key={t.table_name} className="flat-row">
                <span className="icon">{ICONS.Database(14)}</span>
                <span className="name">
                  {schemaName}.{t.table_name}
                  {t.display_label && (
                    <span className="label-kr">{t.display_label}</span>
                  )}
                </span>
                <span className="cnt">—</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VIEWS 섹션 */}
      {views.length > 0 && (
        <div className="flat-section">
          <div className="flat-section-title views">
            <span className="icon">{ICONS.Eye(14)}</span>
            <span>VIEWS</span>
            <span className="count">{views.length}</span>
          </div>
          <div className="flat-grid">
            {views.map(v => (
              <div key={v.table_name} className="flat-row is-view">
                <span className="icon">{ICONS.Eye(14)}</span>
                <span className="name">
                  {schemaName}.{v.table_name}
                  {v.display_label && (
                    <span className="label-kr">{v.display_label}</span>
                  )}
                </span>
                <span className="cnt">—</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 빈 상태 (라이브 데이터 0건) */}
      {ready && totalCount === 0 && (
        <div className="board-empty">
          <div className="empty-mark">◐</div>
          <div className="empty-title">메타 데이터 없음</div>
          <div className="empty-mono">{schemaName}</div>
        </div>
      )}

      {/* 범례 */}
      {totalCount > 0 && (
        <div className="flat-legend">
          <div className="lg">
            <span className="swatch"></span>
            <span className="legend-icon green">{ICONS.Database(12)}</span>
            TABLE — 실 데이터 보유
          </div>
          <div className="lg">
            <span className="swatch vw"></span>
            <span className="legend-icon amber">{ICONS.Eye(12)}</span>
            VIEW — 통합 조회 본질
          </div>
        </div>
      )}
    </div>
  )
}
